import { createActions } from 'redux-actions';
import { request, create } from '../../services';
import { loadPhaseItems } from 'actionCreators';

const {
  phaseResourceSuccess,
  phaseResourceFailure,
  phaseResourceRequest,
} = createActions({
  PHASE_RESOURCE_SUCCESS: [json => ({ phase: json.phase }), json => json.meta],
}, 'PHASE_RESOURCE_FAILURE', 'PHASE_RESOURCE_REQUEST');

export const loadPhase = (id, optimisticState = {}) => (dispatch) => {
  dispatch(phaseResourceRequest({ id, optimisticState }));
  return request(`phases/${id}`).then(({ json }) => {
    dispatch(phaseResourceSuccess(json));
    dispatch(loadPhaseItems(json.phase.purchase_orders, id, 'purchaseOrders'));
  }).catch(error => dispatch(phaseResourceFailure(error)));
};


const {
  phaseResourceCreateRequest,
  phaseResourceCreateSuccess,
  phaseResourceCreateFailure,
} = createActions({
  PHASE_RESOURCE_CREATE_SUCCESS: [json => ({ phase: json.phase }), json => json.meta],
}, 'PHASE_RESOURCE_CREATE_FAILURE', 'PHASE_RESOURCE_CREATE_REQUEST');

export const createPhase = phase => (dispatch) => {
  dispatch(phaseResourceCreateRequest(phase));
  return create('phase', phase).then(({ json }) => {
    dispatch(phaseResourceCreateSuccess(json));
    dispatch(loadPhaseItems(json.phase.purchase_orders, phase.id, 'purchaseOrders'));
  }).catch(error => dispatch(phaseResourceCreateFailure(error)));
};


const {
  phaseResourceCopyRequest,
  phaseResourceCopySuccess,
  phaseResourceCopyFailure,
} = createActions({
  PHASE_RESOURCE_COPY_SUCCESS: [json => ({ phase: json.phase }), json => json.meta],
}, 'PHASE_RESOURCE_COPY_FAILURE', 'PHASE_RESOURCE_COPY_REQUEST');

export function copyPhase(phaseID, body) {
  return (dispatch) => {
    dispatch(phaseResourceCopyRequest());
    return request(`phases/${phaseID}/copy`, { method: 'PUT', body })
      .then(({ json }) => dispatch(phaseResourceCopySuccess(json, phaseID)))
      .catch(error => dispatch(phaseResourceCopyFailure(error)));
  };
}


const {
  phaseResourceUpdateRequest,
  phaseResourceUpdateSuccess,
  phaseResourceUpdateFailure,
} = createActions({
  PHASE_RESOURCE_UPDATE_SUCCESS: [json => ({ phase: json.phase }), json => json.meta],
}, 'PHASE_RESOURCE_UPDATE_FAILURE', 'PHASE_RESOURCE_UPDATE_REQUEST');

export const updatePhase = (phase, { actionType = 'update', communication = 'none' }) => (dispatch) => {
  dispatch(phaseResourceUpdateRequest(phase));
  return request(`phases/${phase.id}`, {
    method: 'PUT',
    body: { phase, action_type: actionType, communication },
  })
    .then(({ json }) => dispatch(phaseResourceUpdateSuccess(json)))
    .catch(error => dispatch(phaseResourceUpdateFailure(error)));
};


export const clearValidation = () => ({ type: 'PHASE_RESOURCE_CLEAR_VALIDATION' });

const {
  addEventToPhaseRequest,
  addEventToPhaseSuccess,
  addEventToPhaseFailure,
} = createActions({
  ADD_EVENT_TO_PHASE_SUCCESS: (json, phaseID) => ({ event: json.event, phaseID }),
}, 'ADD_EVENT_TO_PHASE_FAILURE', 'ADD_EVENT_TO_PHASE_REQUEST');

export const addEventToPhase = (phaseID, event) => (dispatch) => {
  dispatch(addEventToPhaseRequest(event));
  return request(`phases/${phaseID}/events`, { method: 'POST', body: { event } })
    .then(({ json }) => dispatch(addEventToPhaseSuccess(json, phaseID)))
    .catch(errors => dispatch(addEventToPhaseFailure(errors)));
};


const {
  overridePhaseStatusRequest,
  overridePhaseStatusSuccess,
  overridePhaseStatusFailure,
} = createActions({
  OVERRIDE_PHASE_STATUS_SUCCESS: (json, phaseID) => ({ phase: json.phase, phaseID }),
}, 'OVERRIDE_PHASE_STATUS_REQUEST', 'OVERRIDE_PHASE_STATUS_FAILURE');

export function overridePhaseStatus(phaseID, data, addtlData = { source: 'other', delete_appointments: 'no' }) {
  return (dispatch) => {
    dispatch(overridePhaseStatusRequest());
    return request(`phases/${phaseID}/status`, { method: 'PATCH', body: { ...data, ...addtlData } })
      .then(({ json }) => dispatch(overridePhaseStatusSuccess(json, phaseID)))
      .catch(error => dispatch(overridePhaseStatusFailure(error)));
  };
}


const {
  addFlagToPhaseRequest,
  addFlagToPhaseSuccess,
  addFlagToPhaseFailure,
} = createActions({
  ADD_FLAG_TO_PHASE_SUCCESS: (json, phaseID) => ({ phase: json.phase, phaseID }),
}, 'ADD_FLAG_TO_PHASE_REQUEST', 'ADD_FLAG_TO_PHASE_FAILURE');

export function addFlagToPhase(phaseID, flag) {
  return (dispatch) => {
    dispatch(addFlagToPhaseRequest());
    return request(`phases/${phaseID}/flags`, { method: 'POST', body: { flag } })
      .then(({ json }) => dispatch(addFlagToPhaseSuccess(json, phaseID)))
      .catch(error => dispatch(addFlagToPhaseFailure(error)));
  };
}


const {
  removeFlagFromPhaseRequest,
  removeFlagFromPhaseSuccess,
  removeFlagFromPhaseFailure,
} = createActions({
  REMOVE_FLAG_FROM_PHASE_SUCCESS: (json, phaseID) => ({ phase: json.phase, phaseID }),
}, 'REMOVE_FLAG_FROM_PHASE_REQUEST', 'REMOVE_FLAG_FROM_PHASE_FAILURE');

export function removeFlagFromPhase(phaseID, flag) {
  return (dispatch) => {
    dispatch(removeFlagFromPhaseRequest());
    return request(`phases/${phaseID}/flags/${flag.id}`, { method: 'DELETE', body: { flag } })
      .then(({ json }) => dispatch(removeFlagFromPhaseSuccess(json, phaseID)))
      .catch(error => dispatch(removeFlagFromPhaseFailure(error)));
  };
}

const {
  updatePhaseTaskRequest,
  updatePhaseTaskSuccess,
  updatePhaseTaskFailure,
} = createActions({
  UPDATE_PHASE_TASK_SUCCESS: (json, phaseID) => ({ phase: json.phase, phaseID }),
  UPDATE_PHASE_TASK_FAILURE: [error => error, (_, taskID) => ({ taskID })],
}, 'UPDATE_PHASE_TASK_REQUEST');

export function updatePhaseTask(phaseID, taskID, body) {
  return (dispatch) => {
    dispatch(updatePhaseTaskRequest(taskID));
    return request(`phases/${phaseID}/tasks/${taskID}`, { method: 'PUT', body })
      .then(({ json }) => dispatch(updatePhaseTaskSuccess(json, phaseID)))
      .catch(error => dispatch(updatePhaseTaskFailure(error, taskID)));
  };
}

const {
  assignPhaseEstimatorRequest,
  assignPhaseEstimatorSuccess,
  assignPhaseEstimatorFailure,
} = createActions({
  ASSIGN_PHASE_ESTIMATOR_SUCCESS: (json, phaseID) => ({
    phase: json.phase,
    phaseID,
  }),
  ASSIGN_PHASE_ESTIMATOR_FAILURE: [
    error => error,
    (_, taskID) => ({ taskID }),
  ],
}, 'ASSIGN_PHASE_ESTIMATOR_REQUEST');

export function assignPhaseEstimator(phaseID, body) {
  return (dispatch) => {
    dispatch(assignPhaseEstimatorRequest());
    return request(`phases/${phaseID}/estimator`, { method: 'PUT', body })
      .then(({ json }) => dispatch(assignPhaseEstimatorSuccess(json, phaseID)))
      .catch(error => dispatch(assignPhaseEstimatorFailure(error)));
  };
}

const {
  assignPhaseInstallerRequest,
  assignPhaseInstallerSuccess,
  assignPhaseInstallerFailure,
} = createActions({
  ASSIGN_PHASE_INSTALLER_SUCCESS: (json, phaseID) => ({
    phase: json.phase,
    phaseID,
  }),
  ASSIGN_PHASE_INSTALLER_FAILURE: [
    error => error,
    (_, taskID) => ({ taskID }),
  ],
}, 'ASSIGN_PHASE_INSTALLER_REQUEST');

export function assignPhaseInstaller(phaseID, body) {
  return (dispatch) => {
    dispatch(assignPhaseInstallerRequest());
    return request(`phases/${phaseID}/installer`, { method: 'PUT', body })
      .then(({ json }) => dispatch(assignPhaseInstallerSuccess(json, phaseID)))
      .catch(error => dispatch(assignPhaseInstallerFailure(error)));
  };
}

const {
  estimatorListByWorkroomRequest,
  estimatorListByWorkroomSuccess,
  estimatorListByWorkroomFailure,
} = createActions({
  ESTIMATOR_LIST_BY_WORKROOM_SUCCESS: json => ({
    estimatorListByWorkroom: json.estimators,
  }),
}, 'ESTIMATOR_LIST_BY_WORKROOM_REQUEST', 'ESTIMATOR_LIST_BY_WORKROOM_FAILURE');

export function requestEstimatorListByWorkroom(workroomID) {
  return (dispatch) => {
    dispatch(estimatorListByWorkroomRequest());
    return request(
      `estimators?is_active=true&for_workrooms[0]=${workroomID}&assign_estimator=true`,
    ).then(({ json }) => dispatch(estimatorListByWorkroomSuccess(json)))
     .catch(error => dispatch(estimatorListByWorkroomFailure(error)));
  };
}

const {
  installersForAssignmentRequest,
  installersForAssignmentSuccess,
  installersForAssignmentFailure,
} = createActions({
  INSTALLERS_FOR_ASSIGNMENT_SUCCESS: json => ({
    installersForAssignment: json.installers,
  }),
}, 'INSTALLERS_FOR_ASSIGNMENT_REQUEST', 'INSTALLERS_FOR_ASSIGNMENT_FAILURE');

export function requestInstallersForAssignment(workroomID, zipCode) {
  return (dispatch) => {
    dispatch(installersForAssignmentRequest());
    return request(
      `installers/for_assignment/?is_active=true&workroom_id=${workroomID}&assign_installer=true&zip_code=${zipCode}`,
    ).then(({ json }) => dispatch(installersForAssignmentSuccess(json)))
     .catch(error => dispatch(installersForAssignmentFailure(error)));
  };
}

const {
  phaseAppointmentsRequest,
  phaseAppointmentsSuccess,
  phaseAppointmentsFailure,
} = createActions({
  PHASE_APPOINTMENTS_SUCCESS: json => ({
    appointments: json.appointments,
  }),
}, 'PHASE_APPOINTMENTS_REQUEST', 'PHASE_APPOINTMENTS_FAILURE');

export function requestPhaseAppointments(phaseID) {
  return (dispatch) => {
    dispatch(phaseAppointmentsRequest());
    return request(`phases/${phaseID}/appointments`)
      .then(({ json }) => dispatch(phaseAppointmentsSuccess(json)))
      .catch(error => dispatch(phaseAppointmentsFailure(error)));
  };
}

const {
  cancelPhaseRequest,
  cancelPhaseSuccess,
  cancelPhaseFailure,
} = createActions({
  CANCEL_PHASE_SUCCESS: json => ({
    phase: json.phase,
  }),
}, 'CANCEL_PHASE_REQUEST', 'CANCEL_PHASE_FAILURE');

export function cancelPhase(phaseID, data) {
  return (dispatch) => {
    dispatch(cancelPhaseRequest());
    return request(`phases/${phaseID}/cancellation`, { method: 'POST', body: data })
      .then(({ json }) => {
        dispatch(cancelPhaseSuccess(json));
        dispatch(requestPhaseAppointments(phaseID));
      })
      .catch(error => dispatch(cancelPhaseFailure(error)));
  };
}

const {
  verifyPhaseAssessmentRequest,
  verifyPhaseAssessmentSuccess,
  verifyPhaseAssessmentFailure,
} = createActions({
  VERIFY_PHASE_ASSESSMENT_SUCCESS: json => ({ phase: json.phase }),
}, 'VERIFY_PHASE_ASSESSMENT_REQUEST', 'VERIFY_PHASE_ASSESSMENT_FAILURE');

export function verifyPhaseAssessment(phaseData) {
  return (dispatch) => {
    dispatch(verifyPhaseAssessmentRequest());
    return request(`phases/${phaseData.id}/verify_assessment`, { method: 'PUT', body: phaseData })
      .then(({ json }) => dispatch(verifyPhaseAssessmentSuccess(json)))
      .catch(error => dispatch(verifyPhaseAssessmentFailure(error)));
  };
}

const {
  syncPhaseImsStatusRequest,
  syncPhaseImsStatusSuccess,
  syncPhaseImsStatusFailure,
} = createActions({
  SYNC_PHASE_IMS_STATUS_SUCCESS: (json, phaseID) => ({ phase: json.phase, phaseID }),
}, 'SYNC_PHASE_IMS_STATUS_REQUEST', 'SYNC_PHASE_IMS_STATUS_FAILURE');

export function syncPhaseImsStatus(phaseID) {
  return (dispatch) => {
    dispatch(syncPhaseImsStatusRequest());
    return request(`phases/${phaseID}/ims_status_update`, { method: 'PUT' })
      .then(({ json }) => dispatch(syncPhaseImsStatusSuccess(json, phaseID)))
      .catch(error => dispatch(syncPhaseImsStatusFailure(error)));
  };
}

// Unlike loadPhase, this doesn't dispatch PHASE_RESOURCE_REQUEST/SUCCESS, so it doesn't toggle
// `loaded` and force PhaseShow into its full-page <Loading /> state on every tick. Used to poll
// for a phase field (e.g. ims_status) changing in the background without tearing down the page.
export function pollPhaseImsStatus(phaseID) {
  return dispatch =>
    request(`phases/${phaseID}`)
      .then(({ json }) => dispatch(syncPhaseImsStatusSuccess(json, phaseID)))
      .catch(error => dispatch(syncPhaseImsStatusFailure(error)));
}

const {
  createSupportRequestRequest,
  createSupportRequestSuccess,
  createSupportRequestFailure,
} = createActions({
  CREATE_SUPPORT_REQUEST_SUCCESS: json => ({ phase: json.phase }),
}, 'CREATE_SUPPORT_REQUEST_REQUEST', 'CREATE_SUPPORT_REQUEST_FAILURE');

export function createSupportRequest(phaseID, supportRequest) {
  return (dispatch) => {
    dispatch(createSupportRequestRequest());
    return request(`phases/${phaseID}/support_requests`, {
      method: 'POST',
      body: { support_request: supportRequest },
    })
      .then(({ json }) => dispatch(createSupportRequestSuccess(json || {})))
      .catch(error => dispatch(createSupportRequestFailure(error)));
  };
}



// WEBPACK FOOTER //
// ./client/app/shared/actionCreators/phase.js